import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { CalendarDays, MapPin, Plane } from "lucide-react";

// Format ISO YYYY-MM-DD to "15 Agustus, 2026" / "August 15, 2026"
function formatDepartureDate(iso, lang) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const locale = lang === "id" ? "id-ID" : "en-US";
    return d.toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
  } catch { return null; }
}

// Airport code → city name
const AIRPORT_CITY = {
  CGK: "Jakarta",
  HLP: "Jakarta",
  YIA: "Yogyakarta",
  JOG: "Yogyakarta",
  SUB: "Surabaya",
  DPS: "Denpasar",
  BPN: "Balikpapan",
  MES: "Medan",
  UPG: "Makassar",
};

export default function PackageCard({ pkg, index = 0 }) {
  const { lang } = useLang();
  const name = lang === "id" ? pkg.name_id : pkg.name_en;
  const hasPoster = typeof pkg.image === "string" && (
    pkg.image.includes("customer-assets.emergentagent.com") ||
    pkg.image.includes("/api/static/posters/") ||
    /h=1[2-9]\d\d|h=[2-9]\d{3}/.test(pkg.image) // portrait Unsplash (h>=1200)
  );
  const dateLabel = formatDepartureDate(pkg.departure_date, lang) || pkg.departure || "—";
  const startCity = pkg.airport ? (AIRPORT_CITY[pkg.airport] || pkg.airport) : (lang === "id" ? "—" : "—");
  const isFullSeat = pkg.full_booked || pkg.seats_remaining === 0;

  return (
    <article
      data-testid={`package-card-${pkg.slug}`}
      className="group bg-card border border-border rounded-xl overflow-hidden flex flex-col anim-fade-up hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      {/* Image (portrait poster natural height, or wide fallback) */}
      <Link to={`/packages/${pkg.slug}`} data-testid={`view-pkg-${pkg.slug}`} className="block relative">
        {hasPoster ? (
          <div className="relative overflow-hidden">
            <img
              src={pkg.image}
              alt={name}
              loading="lazy"
              className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.03]"
            />

            {/* FULL SEAT overlay */}
            {isFullSeat && (
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/75 to-black/85 backdrop-blur-[2px] flex items-center justify-center" data-testid={`full-seat-overlay-${pkg.slug}`}>
                <div className="text-center px-6">
                  <p className="font-serif text-white text-3xl lg:text-4xl leading-tight italic" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.9), 0 0 24px rgba(0,0,0,0.6)" }}>Alhamdulillah</p>
                  <p className="font-serif text-red-400 text-3xl lg:text-4xl mt-1 leading-tight font-bold" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.9), 0 0 24px rgba(239,68,68,0.4)" }}>Fullseat</p>
                </div>
              </div>
            )}

            {!isFullSeat && pkg.seats_remaining != null && pkg.seats_remaining > 0 && pkg.seats_remaining <= 10 && (
              <div className="absolute top-3 right-3">
                <span data-testid={`seat-badge-limited-${pkg.slug}`} className="inline-flex items-center gap-1 bg-orange-500 text-white px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-md animate-pulse">
                  {lang === "id" ? `Sisa ${pkg.seats_remaining} Seat` : `${pkg.seats_remaining} Seats Left`}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="relative overflow-hidden aspect-[4/3]">
            <img
              src={pkg.image}
              alt={name}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            />
            {isFullSeat && (
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/75 to-black/85 backdrop-blur-[2px] flex items-center justify-center" data-testid={`full-seat-overlay-${pkg.slug}`}>
                <div className="text-center px-6">
                  <p className="font-serif text-white text-3xl lg:text-4xl leading-tight italic" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.9), 0 0 24px rgba(0,0,0,0.6)" }}>Alhamdulillah</p>
                  <p className="font-serif text-red-400 text-3xl lg:text-4xl mt-1 leading-tight font-bold" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.9), 0 0 24px rgba(239,68,68,0.4)" }}>Fullseat</p>
                </div>
              </div>
            )}
            {!isFullSeat && pkg.seats_remaining != null && pkg.seats_remaining > 0 && pkg.seats_remaining <= 10 && (
              <div className="absolute top-3 right-3">
                <span data-testid={`seat-badge-limited-${pkg.slug}`} className="inline-flex items-center gap-1 bg-orange-500 text-white px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-md animate-pulse">
                  {lang === "id" ? `Sisa ${pkg.seats_remaining} Seat` : `${pkg.seats_remaining} Seats Left`}
                </span>
              </div>
            )}
          </div>
        )}
      </Link>

      {/* Content */}
      <div className="p-5 flex flex-col gap-4 flex-1">
        <Link to={`/packages/${pkg.slug}`} className="hover:text-accent transition-colors">
          <h3 className="font-serif text-lg lg:text-xl text-primary leading-snug">{name}</h3>
        </Link>

        {/* Meta list — labeled (parenthesized) like ptbatik.co.id */}
        <ul className="flex flex-col gap-2 text-sm text-foreground/80">
          <li className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-accent shrink-0" />
            <span>
              <span className="text-muted-foreground">({lang === "id" ? "Keberangkatan" : "Departure"})</span> {dateLabel}
            </span>
          </li>
          <li className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-accent shrink-0" />
            <span>
              <span className="text-muted-foreground">({lang === "id" ? "Start" : "From"})</span> {startCity}
            </span>
          </li>
          {pkg.airline && (
            <li className="flex items-center gap-2">
              <Plane className="h-4 w-4 text-accent shrink-0" />
              <span>
                <span className="text-muted-foreground">({lang === "id" ? "Maskapai" : "Airline"})</span> {pkg.airline}
              </span>
            </li>
          )}
        </ul>

        {/* Full-width gold "SELENGKAPNYA" button */}
        <Link
          to={`/packages/${pkg.slug}`}
          data-testid={`selengkapnya-${pkg.slug}`}
          className="mt-auto block text-center bg-accent text-primary hover:bg-[#c48f1e] hover:text-white rounded-lg py-3 text-xs font-bold uppercase tracking-widest transition-colors"
        >
          {lang === "id" ? "Selengkapnya" : "Learn More"}
        </Link>
      </div>
    </article>
  );
}
